<template>
  <v-data-table
    :headers="headers"
    :items="formatItems"
    calculate-widths
    :no-data-text="$t('page-rules.manage.section-seasonal-rules.no-rules')"
  >
    <template v-slot:header.status="{ header }">
      {{ header.text }}
      <primary-tool-tip>
        <span>Status indicates the following states:</span><br>
        <ul>
          <li><strong>Green/Active</strong> - The settings are setup and the rule is working correctly</li>
          <li><strong>Orange/Inactive</strong> - You have not completed setup and need to complete the relevant scenarios</li>
        </ul>
      </primary-tool-tip>
    </template>
    <template v-slot:header.checkInDays="{ header }">
      {{ header.text }}
      <primary-tool-tip>
        <span><v-icon color="white" x-small class="mr-2">mdi-account-switch-outline</v-icon>Changeover days</span><br>
      </primary-tool-tip>
    </template>

    <template v-slot:item.dates="{ item }">
      <v-chip v-for="date in [...item.dates].splice(0, 1)" :key="date" class="mb-1" small>{{ date }}</v-chip>
      <v-chip small v-if="item.dates.length > 1">
        <primary-tool-tip icon="mdi-dots-horizontal">
          <span v-for="date in item.dates" :key="date"> {{ date }} <br /> </span>
        </primary-tool-tip>
      </v-chip>
    </template>
    <template v-slot:item.checkInDays="{ item }">
      <v-chip :title="$t('global-terms.changeover-day')" small class="mr-1 mb-1" v-for="day in item.checkInDays" :key="day.day">{{ day.day }} <v-icon v-if="day.turnaround" class="ml-1" x-small>mdi-account-switch-outline</v-icon></v-chip>
    </template>
    <template v-slot:item.status="{ item }">
      <v-chip
        :color="item.status ? 'green' : 'warning'"
        text-color="white"
        small
      >
        {{ item.status ? $t('global-terms.active') : $t('global-terms.inactive') }}
      </v-chip>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-menu
        bottom
        left
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list class="options-list">
          <v-list-item>
            <v-list-item-title>
              <router-link :to="{name: 'place-rules-seasonal-edit', params: {ruleId: item.id}}" :title="$t('page-rules.manage.section-seasonal-rules.actions.edit.label')">
                <v-icon left small>mdi-pencil</v-icon><span v-html="$t('global-terms.edit')"></span>
              </router-link>
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
              <confirmation
                title="Delete Seasonal Rule Confirmation"
                :submitHandler="deleteSubmitHandler"
                :entity="item"
                >
                <template v-slot:activator="props">
                  <v-list-item-title @click.stop="props.showDialog" class="red--text">
                    <v-icon left small class="red--text">mdi-delete</v-icon>{{ $t('global-terms.delete') }}
                  </v-list-item-title>
                </template>
                <template v-slot:body>
                    <p v-html="$t('page-rules.manage.section-seasonal-rules.actions.delete.confirmation-body')"></p>
                </template>
              </confirmation>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
</template>
<script>
import { mapActions } from 'pinia'
import { clone } from 'ramda'
import { formatCheckInDays } from '@/helpers/bookingRules'
import { useBookingRuleStore } from '@/stores'
import moment from 'moment'
import Confirmation from '@/components/dialogs/Confirmation'
import PrimaryToolTip from '../../../../../components/tooltips/PrimaryToolTip.vue'

export default {
  data () {
    return {
      headers: [
        { text: 'Season', value: 'name', sortable: false, width: '18%' },
        { text: 'Season Periods', value: 'dates', sortable: false, width: '10%' },
        { text: 'Check in days', value: 'checkInDays', sortable: false, width: '50%' },
        { text: 'Rate', value: 'rate', sortable: false, width: '8%' },
        { text: 'Status', value: 'status', sortable: false, width: '11.25%' },
        { align: 'end', value: 'actions', sortable: false, width: '0.25%' },
      ],
    }
  },
  props: {
    place: {
      type: Object,
      required: true,
    },
    seasonalBookingRules: {
      type: Array,
      required: true,
    },
  },
  methods: {
    clone,
    ...mapActions(useBookingRuleStore, ['httpDeleteSeasonalBookingRule']),
    formatCheckInDays,
    deleteSubmitHandler (rule) {
      const placeClone = this.clone(this.place)
      return this.httpDeleteSeasonalBookingRule({ seasonalBookingRule: rule, place: placeClone })
    },
  },
  computed: {
    formatItems () {
      return this.seasonalBookingRules.map(rule => {
        return {
          id: rule.id,
          name: rule.label,
          dates: rule.seasonalBookingRuleDates.map(date => `${moment(date.dates.start).format('DD/MM/YY')} - ${moment(date.dates.end).format('DD/MM/YY')}`),
          checkInDays: this.formatCheckInDays(rule.stayLengths),
          status: rule.active,
          rule,
        }
      })
    },
  },
  components: {
    Confirmation,
    PrimaryToolTip,
  },
}
</script>
<style lang="scss">
.v-data-table .v-data-table-header,
.v-data-table thead  {
  background-color: #eeeeee !important;
  border-bottom: #eeeeee !important;
}

.v-data-table .v-data-table-header th {
  font-weight: 500;
  color: #000 !important;
  font-size: 15px;
}
.v-data-table td {
  padding: 20px 16px;
  height: 85px;
}
.options-list {
  .v-list-item {
    display: block;

    &:hover {
      cursor: pointer;
      background: rgb(237, 237, 237);
    }
    .v-list-item__title,
    .v-list-item__title > a {
      display: flex;
      align-items: center;
      height: 48px;
      width: 100%;
      text-decoration: none;
      color: #000;
    }
  }
}
</style>
